import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaToggleOff, FaToggleOn, FaSpinner, FaTimes, FaSave } from 'react-icons/fa';
import Button from '../../components/Button';
import Table from '../../components/Table';
import Input from '../../components/Input';
import PageHeader from '../../components/PageHeader';
import Skeleton from '../../components/Skeleton';
import { planService } from '../../services/planService';
import { assistantUsageService } from '../../services/assistantUsageService';
import { planCaps, planCapsLabel } from '../../utils/assistantPlans';
import { toastSuccess, toastError } from '../../utils/toast';
import { swalConfirm } from '../../utils/swal';

const EMPTY_FORM = { name: '', turns: '', minutes: '', usd: '' };
const optional = (v) => (v == null ? '' : String(v));
const invalid = (v) => v !== '' && (isNaN(v) || Number(v) < 0);

const PlanModal = ({ isOpen, editingPlan, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(editingPlan
      ? {
          name: editingPlan.name,
          turns: optional(editingPlan.assistant_text_turns_monthly),
          minutes: optional(editingPlan.assistant_voice_minutes_monthly),
          usd: optional(editingPlan.assistant_cost_cap_usd_monthly),
        }
      : EMPTY_FORM
    );
    setErrors({});
  }, [isOpen, editingPlan]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Requerido';
    if (invalid(form.turns)) e.turns = 'Número igual o mayor que 0, o vacío';
    if (invalid(form.minutes)) e.minutes = 'Número igual o mayor que 0, o vacío';
    if (invalid(form.usd)) e.usd = 'Número igual o mayor que 0, o vacío';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = planCaps(form);
      if (editingPlan) {
        await planService.update(editingPlan.id, payload);
        toastSuccess('Plan actualizado');
      } else {
        await planService.create(payload);
        toastSuccess('Plan creado');
      }
      onSaved();
      onClose();
    } catch (err) {
      toastError(err.message || 'Error al guardar plan');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-surface-card border border-white/[0.06] rounded-xl shadow-2xl w-full max-w-md mx-4" role="dialog" aria-modal="true" aria-label={editingPlan ? 'Editar plan' : 'Nuevo plan'}>
        <div className="flex items-center justify-between p-5 border-b border-white/[0.07]">
          <h2 className="font-semibold" style={{ color: 'rgb(var(--fg))' }}>{editingPlan ? 'Editar plan' : 'Nuevo plan'}</h2>
          <button onClick={onClose} aria-label="Cerrar" style={{ color: 'rgb(var(--fg-faint))' }} className="hover:text-white transition-colors"><FaTimes size={16} /></button>
        </div>
        <div className="p-5 space-y-2">
          <Input label="Nombre del plan *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} error={errors.name} />
          <p className="text-xs pb-2" style={{ color: 'rgb(var(--fg-dim))' }}>
            Lo que puede usar del asistente de smooth (chat y voz) un cliente con este plan, <strong>por mes</strong> (hora de México). Vacío = sin tope. Al 80 % se le avisa y al 100 % se detiene lo que se pasó.
          </p>
          <Input label="Mensajes de texto al mes" type="number" min="0" placeholder="Ej: 1000" value={form.turns} onChange={e => setForm(p => ({ ...p, turns: e.target.value }))} error={errors.turns} />
          <Input label="Minutos de voz al mes" type="number" min="0" placeholder="Ej: 60" value={form.minutes} onChange={e => setForm(p => ({ ...p, minutes: e.target.value }))} error={errors.minutes} />
          <Input label="Tope de costo al mes (USD)" type="number" min="0" placeholder="Ej: 20" value={form.usd} onChange={e => setForm(p => ({ ...p, usd: e.target.value }))} error={errors.usd} />
          <p className="text-xs" style={{ color: 'rgb(var(--fg-faint))' }}>
            El tope en USD es la red de seguridad: corta texto y voz aunque aún queden mensajes o minutos. Después puedes ajustar el límite de un cliente a mano en «Uso del asistente» sin cambiarle el plan.
          </p>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-white/[0.07]">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <FaSpinner className="animate-spin mr-2" /> : <FaSave className="mr-2" />}
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminPlanesPage = () => {
  const [plans, setPlans] = useState([]);
  const [clientCounts, setClientCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      setPlans(await planService.getAll());
      // Clientes (proyectos) por plan: salen del resumen del asistente; si el script 50 aún no está, se muestra 0
      try {
        const counts = {};
        ((await assistantUsageService.overview()) || []).forEach((p) => { if (p.plan_id) counts[p.plan_id] = (counts[p.plan_id] || 0) + 1; });
        setClientCounts(counts);
      } catch { setClientCounts({}); }
    } catch (err) {
      toastError(err.message || 'Error al cargar planes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleDeactivate = async (plan) => {
    const { isConfirmed } = await swalConfirm(
      `¿Desactivar "${plan.name}"?`,
      'Los clientes que ya tienen este plan conservan sus topes; solo deja de ofrecerse para asignarlo. Solo admins pueden desactivar planes.',
      'Sí, desactivar',
      { input: null, html: null, footer: null, showDenyButton: false }
    );
    if (!isConfirmed) return;
    try {
      await planService.deactivate(plan.id);
      toastSuccess('Plan desactivado');
      fetchPlans();
    } catch (err) {
      toastError(err.message || 'Error al desactivar plan');
    }
  };

  const cell = (text) => <span className="text-sm font-mono" style={{ color: 'rgb(var(--fg))' }}>{text}</span>;
  const columns = [
    {
      key: 'name', title: 'Plan', sortable: true,
      render: (v, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: 'rgb(var(--fg))' }}>{v}</span>
          {!row.is_active && <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgb(var(--fg) / 0.06)', color: 'rgb(var(--fg-dim))' }}>Inactivo</span>}
        </div>
      ),
    },
    { key: 'assistant_text_turns_monthly', title: 'Mensajes / mes', sortable: true, render: (v) => cell(planCapsLabel.turns(v)) },
    { key: 'assistant_voice_minutes_monthly', title: 'Voz / mes', sortable: true, render: (v) => cell(planCapsLabel.minutes(v)) },
    { key: 'assistant_cost_cap_usd_monthly', title: 'Tope de costo / mes', sortable: true, render: (v) => cell(planCapsLabel.usd(v)) },
    {
      key: 'id', title: 'Clientes', sortable: false,
      render: (v) => {
        const count = clientCounts[v] ?? 0;
        return <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(59,130,246,0.12)', color: '#60A5FA' }}>{count} {count === 1 ? 'cliente' : 'clientes'}</span>;
      },
    },
    {
      key: 'is_active', title: 'Estado', sortable: true,
      render: (v) => v
        ? <span className="text-xs font-medium text-green-400 flex items-center gap-1"><FaToggleOn size={14} /> Activo</span>
        : <span className="text-xs font-medium flex items-center gap-1" style={{ color: 'rgb(var(--fg-dim))' }}><FaToggleOff size={14} /> Inactivo</span>,
    },
    {
      key: 'actions', title: '', sortable: false,
      render: (_, row) => (
        <div className="flex gap-2 justify-end">
          <button onClick={() => { setEditingPlan(row); setModalOpen(true); }} className="p-1.5 rounded-lg text-blue-400 hover:bg-blue-500/10 transition-colors" title="Editar" aria-label={`Editar ${row.name}`}><FaEdit size={14} /></button>
          {row.is_active && (
            <button onClick={() => handleDeactivate(row)} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" style={{ color: 'rgb(var(--fg-dim))' }} title="Desactivar" aria-label={`Desactivar ${row.name}`}><FaToggleOn size={14} /></button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Planes del asistente" subtitle="Topes por mes del asistente de smooth (chat y voz). Cada cliente usa un plan, o se le ajusta el límite a mano en «Uso del asistente» sin cambiarle el plan.">
        <Button variant="primary" size="lg" onClick={() => { setEditingPlan(null); setModalOpen(true); }} leftIcon={<FaPlus size={11} />}>
          Nuevo plan
        </Button>
      </PageHeader>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        style={{ background: 'rgb(var(--panel))', border: '1px solid rgb(var(--fg) / 0.07)', boxShadow: 'var(--shadow)', borderRadius: '24px', padding: '24px' }}
      >
        {loading ? <Skeleton.TableRows rows={5} cols={6} /> : (
          <Table data={plans} columns={columns} options={{ pagination: false, showSearch: false, emptyMessage: 'No hay planes configurados' }} />
        )}
      </motion.div>

      <PlanModal isOpen={modalOpen} editingPlan={editingPlan} onClose={() => setModalOpen(false)} onSaved={fetchPlans} />
    </div>
  );
};

export default AdminPlanesPage;
